import { Close } from '@mui/icons-material';
import { Alert, IconButton, Snackbar, SnackbarProps } from '@mui/material';
import { ReactNode, SyntheticEvent, useEffect, useMemo, useState } from 'react';

import SnackbarContext from './SnackbarContext';
import { SnackbarMessage, SnackbarSeverity } from './types';

const defaultSnackbarProps: SnackbarProps = {
  autoHideDuration: 3000,
};

const defaultOptions = (message: string): SnackbarMessage => ({
  key: new Date().getTime().toString(),
  message,
  severity: SnackbarSeverity.INFO,
  snackbarProps: defaultSnackbarProps,
});

const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<{
    messageQueue: SnackbarMessage[];
    currentMessage?: SnackbarMessage;
    open: boolean;
  }>({
    messageQueue: [],
    currentMessage: undefined,
    open: false,
  });

  useEffect(() => {
    const { messageQueue, currentMessage, open } = state;
    if (!messageQueue.length) return;

    if (!currentMessage && messageQueue.length) {
      setState((prev) => ({
        ...prev,
        currentMessage: { ...messageQueue[0] },
        messageQueue: prev.messageQueue.slice(1),
        open: true,
      }));
      return;
    }

    if (open) {
      setState((prev) => ({ ...prev, open: false }));
    }
  }, [state]);

  const ctx = useMemo(() => {
    const snack = (options: string | SnackbarMessage) => {
      let augmented: SnackbarMessage;
      if (typeof options === 'string') {
        augmented = defaultOptions(options);
      } else {
        augmented = { ...defaultOptions(''), ...options };
      }
      setState((prev) => ({
        ...prev,
        messageQueue: [...prev.messageQueue, augmented],
      }));
    };
    return { snack };
  }, []);

  const handleClose = (_: SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setState((prev) => ({ ...prev, open: false }));
  };

  const handleExited = () => {
    setState((prev) => ({ ...prev, currentMessage: undefined }));
  };

  const { key, message, severity, snackbarProps } = state.currentMessage ?? {};
  return (
    <SnackbarContext.Provider value={ctx}>
      {children}
      <Snackbar
        key={key}
        open={state.open}
        autoHideDuration={snackbarProps?.autoHideDuration}
        onClose={handleClose}
        TransitionProps={{ onExited: handleExited }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            sx={{ p: 0.5 }}
            onClick={handleClose}
          >
            <Close />
          </IconButton>
        }
      >
        <Alert
          onClose={handleClose}
          variant="filled"
          severity={severity}
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;
